import { ContentCopy } from "@mui/icons-material";
import {
  Box,
  IconButton,
  ImageList,
  ImageListItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { StatusPill, StyledCard, VideoPlayer } from "components";
import { alertTypeLabelKey } from "config/constants";
import { useTranslation } from "hooks";
import { useSnackbar } from "notistack";
import { ReactNode, useCallback, useMemo } from "react";
import { ThreatModel } from "types";
import { dateTimeFormat, formatNumbers, getBoxLabel, getCameraLabel, getSignedUrl, getSiteLabel } from "utils";

const tableCellSx = {
  py: 1.5,
  px: 3,
  borderColor: COLORS.surface.border,
  color: COLORS.surface.primaryText,
  fontSize: 14,
  verticalAlign: "middle"
};

const labelSx = {
  color: COLORS.surface.secondaryText,
  fontWeight: 500,
  mb: 0.75,
  lineHeight: 1.4,
  fontSize: 12,
  display: "block"
};

const sectionHeaderSx = {
  px: 3,
  py: 2,
  pb: 1.5,
  borderBottom: `1px solid ${COLORS.surface.border}`
};

const sectionBodySx = {
  px: 3,
  py: 2.5
};

const detailsGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  columnGap: 3,
  rowGap: 2.5,
  alignItems: "start"
};

function ThreatDetailField({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Typography component="span" sx={labelSx}>{label}</Typography>
      <Box minHeight={28} display="flex" alignItems="flex-start" flex={1}>
        {typeof value === "string" || typeof value === "number" ? (
          <Typography
            variant="body2"
            sx={{ color: COLORS.surface.primaryText, fontWeight: 500, wordBreak: "break-word", lineHeight: 1.5 }}
          >
            {value || "—"}
          </Typography>
        ) : (
          value || (
            <Typography variant="body2" sx={{ color: COLORS.surface.primaryText, fontWeight: 500, lineHeight: 1.5 }}>
              —
            </Typography>
          )
        )}
      </Box>
    </Box>
  );
}

function ModalityPills({ threat, t }: { threat: ThreatModel; t: Record<string, string> }) {
  const pills = [];
  if (threat.threatType?.video) {
    pills.push({
      key: "video",
      label: t["Video"],
      bg: "rgba(30, 152, 217, 0.12)",
      color: COLORS.primary
    });
  }
  if (threat.threatType?.audio) {
    pills.push({
      key: "audio",
      label: t["Audio"],
      bg: "#F5F3FF",
      color: "#8B5CF6"
    });
  }
  if (!pills.length) return <Typography variant="body2">—</Typography>;

  return (
    <Box display="flex" flexWrap="wrap" gap={0.75}>
      {pills.map((pill) => (
        <Box
          key={pill.key}
          sx={{
            px: 1.25,
            py: 0.35,
            borderRadius: `${COLORS.radius.pill}px`,
            bgcolor: pill.bg,
            color: pill.color,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.5
          }}
        >
          {pill.label}
        </Box>
      ))}
    </Box>
  );
}

function RtspField({ value }: { value?: string }) {
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const onCopy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar(t["Copied to clipboard"] || "Copied to clipboard", { variant: "success" });
    } catch {
      enqueueSnackbar(t["Failed to copy"] || "Failed to copy", { variant: "error" });
    }
  }, [value, enqueueSnackbar, t]);

  if (!value) return <Typography variant="body2">—</Typography>;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      width="100%"
      sx={{
        px: 1.5,
        py: 1,
        bgcolor: COLORS.surface.tableHeader,
        borderRadius: `${COLORS.radius.sm}px`,
        border: `1px solid ${COLORS.surface.border}`,
        boxSizing: "border-box"
      }}
    >
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          color: COLORS.surface.primaryText,
          fontWeight: 500,
          wordBreak: "break-all",
          fontSize: 13,
          lineHeight: 1.5
        }}
      >
        {value}
      </Typography>
      <Tooltip title={t["Copy"] || "Copy"}>
        <IconButton size="small" onClick={onCopy} sx={{ color: COLORS.surface.secondaryText, flexShrink: 0 }}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function StatusHistoryTable({
  rows,
  t
}: {
  rows: NonNullable<ThreatModel["statusHistory"]>;
  t: Record<string, string>;
}) {
  const columns = [
    { key: "status", label: t["Status"], width: "22%" },
    { key: "user", label: t["Changed By"], width: "18%" },
    { key: "date", label: t["Date Changed"], width: "28%" },
    { key: "note", label: t["Note"], width: "32%" }
  ];

  const historyCellSx = {
    ...tableCellSx,
    px: 1.5,
    overflow: "hidden",
    verticalAlign: "top"
  };

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table
        size="small"
        aria-label="status history"
        sx={{ tableLayout: "fixed", width: "100%", minWidth: 520 }}
      >
        <TableHead>
          <TableRow>
            {columns.map(({ key, label, width }) => (
              <TableCell
                key={key}
                sx={{
                  ...historyCellSx,
                  width,
                  bgcolor: COLORS.surface.tableHeader,
                  borderBottom: `1px solid ${COLORS.surface.border}`,
                  fontWeight: 600,
                  verticalAlign: "middle"
                }}
              >
                <Typography component="span" sx={{ color: COLORS.surface.secondaryText, fontWeight: 600, fontSize: 13 }}>
                  {label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                sx={{
                  ...historyCellSx,
                  textAlign: "center",
                  py: 3,
                  borderBottom: 0,
                  color: COLORS.surface.secondaryText,
                  verticalAlign: "middle"
                }}
              >
                {t["No status history yet"]}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((entry, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ ...historyCellSx, whiteSpace: "nowrap" }}>
                  <StatusPill label={t[entry.status] || entry.status} status={entry.status} />
                </TableCell>
                <TableCell
                  sx={{
                    ...historyCellSx,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis"
                  }}
                  title={entry.userName || undefined}
                >
                  {entry.userName || "—"}
                </TableCell>
                <TableCell
                  sx={{
                    ...historyCellSx,
                    whiteSpace: "normal",
                    wordBreak: "normal",
                    overflowWrap: "anywhere",
                    lineHeight: 1.35
                  }}
                >
                  {dateTimeFormat(entry.changedAt)}
                </TableCell>
                <TableCell
                  sx={{
                    ...historyCellSx,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.35
                  }}
                >
                  {entry.note || "—"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function getThreatSubtitle(threat: ThreatModel, t: Record<string, string>) {
  const typeKey = alertTypeLabelKey(threat.threatType.model);
  const type = t[typeKey] || typeKey;
  const camera = threat.camera?.name || getCameraLabel(threat.camera);
  return t["Threat subtitle template"]
    .replace("{type}", type)
    .replace("{camera}", camera);
}

export default function ThreatDetails({ threat, loading }: { threat?: ThreatModel | null; loading?: boolean }) {
  const t = useTranslation();
  const statusHistory = useMemo(() => {
    if (!threat?.statusHistory?.length) return [];
    return [...threat.statusHistory].reverse();
  }, [threat]);
  const hasImages = Boolean(threat?.image || (threat?.images && threat.images.length > 0));

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" width="100%" height={100} sx={{ borderRadius: `${COLORS.radius.md}px` }} />
        <Skeleton variant="rounded" width="100%" height={280} sx={{ borderRadius: `${COLORS.radius.md}px` }} />
        <Skeleton variant="rounded" width="100%" height={200} sx={{ borderRadius: `${COLORS.radius.md}px` }} />
      </Stack>
    );
  }
  if (!threat) return null;

  const confidence = formatNumbers(threat.percentage, { maximumFractionDigits: 2 }) + "%";

  return (
    <Stack spacing={2}>
      <StyledCard>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
          sx={{ px: 3, py: 2.5 }}
        >
          <Box minWidth={0}>
            <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 22, lineHeight: 1.3 }}>
              {t["Threat #"]}{threat.threatId}
            </Typography>
            <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14, mt: 0.5, lineHeight: 1.5 }}>
              {getThreatSubtitle(threat, t)}
            </Typography>
          </Box>
          <StatusPill label={t[threat.status] || threat.status} status={threat.status} />
        </Box>
      </StyledCard>

      <StyledCard>
        <Box sx={sectionHeaderSx}>
          <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 16, lineHeight: 1.3 }}>
            {t["Threat Details"]}
          </Typography>
        </Box>
        <Box sx={sectionBodySx}>
          <Box sx={detailsGridSx}>
            <ThreatDetailField label={t["Threat ID"]} value={threat.threatId} />
            <ThreatDetailField label={t["Time"]} value={dateTimeFormat(threat.timestamp)} />
            <ThreatDetailField
              label={t["Threat Type"]}
              value={t[alertTypeLabelKey(threat.threatType.model)] || alertTypeLabelKey(threat.threatType.model)}
            />
            <ThreatDetailField label={t["Confidence"]} value={confidence} />
            <ThreatDetailField label={t["Modalities"]} value={<ModalityPills threat={threat} t={t} />} />
            <ThreatDetailField
              label={t["Status"]}
              value={<StatusPill label={t[threat.status] || threat.status} status={threat.status} />}
            />
            <ThreatDetailField label={t["Site"]} value={getSiteLabel(threat.box.site)} />
            <ThreatDetailField label={t["Smart Box"]} value={getBoxLabel(threat.box)} />
            <ThreatDetailField label={t["Camera"]} value={getCameraLabel(threat.camera)} />
            <ThreatDetailField
              label={t["Camera RTSP"]}
              value={<RtspField value={threat.camera?.rtspHost || undefined} />}
            />
          </Box>
        </Box>
      </StyledCard>

      {threat.mediaExpiredAt && !threat.video && !threat.image && !(threat.images && threat.images.length) ? (
        <StyledCard>
          <Box sx={{ px: 3, py: 2 }}>
            <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14 }}>
              {t["Clip expired"] || "Clip expired — media was removed under the retention policy."}
            </Typography>
          </Box>
        </StyledCard>
      ) : null}

      {threat.video ? (
        <StyledCard sx={{ overflow: "hidden" }}>
          <VideoPlayer src={getSignedUrl(threat.video)} />
        </StyledCard>
      ) : null}

      <StyledCard>
        <Box sx={sectionHeaderSx}>
          <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 16, lineHeight: 1.3 }}>
            {t["Status History"]}
          </Typography>
        </Box>
        <StatusHistoryTable rows={statusHistory} t={t} />
      </StyledCard>

      {hasImages && (
        <ImageList variant="masonry" cols={3} gap={8}>
          {threat.image && (
            <ImageListItem key="primary-image">
              <img src={getSignedUrl(threat.image)} alt={threat._id} />
            </ImageListItem>
          )}
          {threat.images?.map((item, i) => (
            <ImageListItem key={`${threat._id}-img-${i}`}>
              <img src={getSignedUrl(item)} alt={`${i}-th-image`} />
            </ImageListItem>
          ))}
        </ImageList>
      )}
    </Stack>
  );
}
